/**
 * Copyright IBM Corp. 2022, 2026
 */

import type { ReactNode } from 'react'
import {
	ButtonColor,
	StandaloneLink,
	Button,
} from '@hashicorp/mds-react/components'
import type { Products } from '@web/product-meta'
import classNames from 'classnames'
import variantCentered from './styles/variant-centered.module.css'
import variantCompact from './styles/variant-compact.module.css'
import variantCompactGrid from './styles/variant-compact-grid.module.css'
import variantLinks from './styles/variant-links.module.css'

const stylesDict = {
	centered: variantCentered,
	compact: variantCompact,
	compactGrid: variantCompactGrid,
	links: variantLinks,
}

interface CallToActionProps {
	heading?: string
	content?: ReactNode
	links?: {
		type?: 'inbound' | 'outbound' | 'anchor' | 'download'
		text: string
		url: string
	}[]
	variant?: 'centered' | 'compact' | 'compactGrid' | 'links'
	product?: 'primary' | 'secondary' | Exclude<Products, 'hashicorp'>
	theme?: 'light' | 'gray' | 'dark' | 'brand'
	className?: string
}

const LINK_TYPE_ICON_MAP = {
	inbound: 'arrow-right',
	outbound: 'external-link',
	download: 'download',
	anchor: 'corner-right-down',
}

function CallToAction({
	heading,
	content,
	links,
	variant = 'centered',
	product = 'primary',
	theme = 'light',
	className,
}: CallToActionProps) {
	const s = stylesDict[variant]
	if (!heading && !content) {
		throw new Error('<CallToAction /> requires either heading or content')
	}
	const hasLinks = links && links.length > 0
	if (hasLinks && links.filter((l) => !l.text || !l.url).length > 0) {
		throw new Error(
			'<CallToAction /> `links` must have both a "text" and a "url" prop',
		)
	}
	return (
		<div className={classNames(s.root, s[`theme-${theme}`], className)}>
			<div className={s.container}>
				{heading && (
					<h2 className={s.heading} data-testid="heading">
						{heading}
					</h2>
				)}
				<div className={s.contentAndLinks}>
					{content && (
						<div
							className={classNames(s.content, { [s.hasHeading]: heading })}
							data-testid="content"
						>
							{content}
						</div>
					)}
					{links && (
						<div className={s.links} data-testid="links">
							{links.map((link, stableIdx) => {
								let color: ButtonColor =
									variant === 'links'
										? 'tertiary'
										: stableIdx === 0
											? product
											: 'secondary'

								if (color === 'tertiary') {
									return (
										<StandaloneLink
											key={stableIdx}
											color="secondary"
											text={link.text}
											href={link.url}
											size="large"
											icon="arrow-right"
											iconPosition="trailing"
										/>
									)
								}

								// @ts-expect-error - TypeScript doesn't allow 'hashicorp', but
								// Dato might still pass it, so rewrite it to 'primary'.
								if (color === 'hashicorp') {
									color = 'primary'
								}

								if (theme === 'brand' && color === 'primary') {
									// `primary` doesn't work on brand, so
									// update it to `secondary`
									color = 'secondary'
								} else if (theme === 'brand' && color === 'secondary') {
									return (
										<StandaloneLink
											key={stableIdx}
											color="secondary-inverted"
											text={link.text}
											href={link.url}
											size="large"
											icon="arrow-right"
											iconPosition="trailing"
										/>
									)
								}

								return (
									<Button
										// eslint-disable-next-line react/no-array-index-key
										key={stableIdx}
										// className={s.button}
										color={color}
										text={link.text}
										href={link.url}
										size="large"
										icon={LINK_TYPE_ICON_MAP[link.type]}
										iconPosition="trailing"
									/>
								)
							})}
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default CallToAction
